import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { emptyFunction } from "constants/defaultValues";
import { addToast } from "components/notifications";
import { useLoaderEffect } from "./useLoaderEffect";
import { useMutation } from "@apollo/client";
import { useReactOidc } from "@axa-fr/react-oidc-context";

export function useQueryWithErrorHandling(query, { onError = emptyFunction, ...props } = {}) {
  const showError = useError();
  const errorHandler = useCallback(
    (error) => {
      onError();
      showError(error);
    },
    [onError, showError]
  );

  const output = useQuery(query, { ...props, onError: errorHandler });
  useLoaderEffect(output?.loading || false);

  return output;
}

export function useClientQueryWithErrorHandling() {
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const showError = useError();

  useLoaderEffect(loading);

  return useCallback(
    async (query, props) => {
      setLoading(true);
      try {
        const output = await client.query({ query, ...props });
        setLoading(false);
        return output;
      } catch (error) {
        showError(error);
        setLoading(false);
        return { loading: false, error };
      }
    },
    [client, showError]
  );
}

export function useMutationWithErrorHandling(mutation, { onError = emptyFunction, ...props } = {}) {
  const showError = useError();
  const errorHandler = useCallback(
    (error) => {
      onError();
      showError(error);
    },
    [onError, showError]
  );

  const output = useMutation(mutation, { ...props, onError: errorHandler });
  useLoaderEffect(output[1]?.loading || false);

  return output;
}

function tryParseJson(str) {
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
}

const generateErrorMessages = (error) => {
  const errorContent = error.extensions?.response?.body;

  if (!errorContent) {
    if (error?.message)
      return [{ message: error?.message + ((error?.path && " :" + JSON.stringify(error?.path)) || "") }];

    return [];
  }

  if (errorContent && Array.isArray(errorContent)) {
    return errorContent.map((x) => ({
      code: x.code,
      message: x.message ?? x.technicalMessage,
      correlationId: x.correlationId,
    }));
  }

  if (typeof errorContent === "string" || errorContent instanceof String) {
    if (errorContent === "") return [];
    const jsonErrorContent = tryParseJson(errorContent);
    if (jsonErrorContent?.title) return [{ message: jsonErrorContent?.title }];
    if (!jsonErrorContent) return [{ message: errorContent }];
  }

  return [];
};

const addErrorToast = (err) => err?.message && addToast(err.message, "error", false, err.correlationId);

const addWarningToast = (err) => err?.message && addToast(err.message, "warning", true);

export const useError = () => {
  const { logout } = useReactOidc();
  return useCallback(
    (error) => {
      const errorToasts = [];
      const warningToasts = [];

      if (!error?.graphQLErrors && !error?.networkError?.result?.errors) {
        error?.message != "" && errorToasts.push({ message: `${error.message}` });
      }

      const graphQLErrors = error?.graphQLErrors ?? [];

      if (graphQLErrors.some((err) => err?.extensions?.code === "UNAUTHENTICATED") && typeof logout === "function") {
        logout();
      }

      graphQLErrors.forEach((err) => {
        err?.extensions?.code
          ? generateErrorMessages(err).forEach((e) =>
              e?.code === "General.WarningException" ? warningToasts.push(e) : errorToasts.push(e)
            )
          : errorToasts.push({ message: `${err.message}` });
      });

      const networkErrors = error?.networkError?.result?.errors ?? [];
      networkErrors.forEach((err) => generateErrorMessages(err).forEach((e) => errorToasts.push(e)));

      if (error?.networkError?.bodyText) {
        errorToasts.push({ message: error?.networkError?.bodyText });
      } else if (error?.networkError?.message) {
        errorToasts.push({ message: error?.networkError?.message });
      }

      errorToasts.forEach((err) => addErrorToast(err));
      warningToasts.forEach((err) => addWarningToast(err));
    },
    [logout]
  );
};
